
import { Component, Inject, Vue } from "vue-property-decorator";
import EventBus from "@models/eventBus";
import ReferralRuleItem from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/Referral/ReferralRuleItem";
import UpsertReferralRuleRequest from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/Referral/UpsertReferralRuleRequest";
import {
  buildUpsertReferralRulePayload,
  defaultReferralRuleUpsertTrigger,
  getOptionName,
  referralActivityOptions,
  referralRewardTypeOptions,
  referralRulesTableHeaders,
  referralTriggerOptions,
} from "./referral-options";

class ReferralRuleFilter {
  isActive: boolean = null;
  trigger: number = null;

  resetToDefault() {
    this.isActive = null;
    this.trigger = null;
  }

  toFilterRequest() {
    return {
      IsActive: this.isActive,
      Trigger: this.trigger,
    };
  }
}

@Component
export default class PageReferralRulesComponent extends Vue {
  @Inject("eventBus") eventBus: EventBus;
  filter: ReferralRuleFilter = new ReferralRuleFilter();
  columnNames = [
    "#",
    "Тип условия",
    referralRulesTableHeaders.threshold,
    referralRulesTableHeaders.windowDays,
    referralRulesTableHeaders.rewardType,
    referralRulesTableHeaders.rewardDays,
    "Правило",
    "",
    "",
    "",
  ];
  triggerOptions = referralTriggerOptions;
  rewardTypeOptions = referralRewardTypeOptions;
  activityOptions = referralActivityOptions;
  form: UpsertReferralRuleRequest = this.createEmptyForm();
  isFormVisible = false;
  isFormSaving = false;

  getDataFunc = this.$api.ReferralService.getRuleListAsync.bind(this.$api.ReferralService);
  removeFunc = this.$api.ReferralService.deleteRuleAsync.bind(this.$api.ReferralService);

  createEmptyForm(): UpsertReferralRuleRequest {
    return new UpsertReferralRuleRequest({
      RuleId: null,
      Trigger: defaultReferralRuleUpsertTrigger,
      Threshold: 1,
      WindowDays: null,
      RewardType: 0,
      RewardDays: 1,
    });
  }

  getTriggerName(trigger: number): string {
    return getOptionName(this.triggerOptions, trigger);
  }

  getRewardTypeName(rewardType: number): string {
    return getOptionName(this.rewardTypeOptions, rewardType);
  }

  openCreateForm() {
    this.form = this.createEmptyForm();
    this.isFormVisible = true;
  }

  openEditForm(item: ReferralRuleItem) {
    this.form = new UpsertReferralRuleRequest({
      RuleId: Number(item.Id),
      Trigger: item.Trigger,
      Threshold: Number(item.Threshold),
      WindowDays: item.WindowDays == null ? null : Number(item.WindowDays),
      RewardType: item.RewardType,
      RewardDays: item.RewardDays == null ? null : Number(item.RewardDays),
    });
    this.isFormVisible = true;
  }

  closeForm() {
    this.isFormVisible = false;
    this.form = this.createEmptyForm();
  }

  startDelete(item: ReferralRuleItem) {
    this.eventBus.deleteEntity.trigger({ id: item.Id });
  }

  async saveRule(request: UpsertReferralRuleRequest) {
    this.isFormSaving = true;
    try {
      const duplicateRule = await this.findDuplicateRule(request);
      if (duplicateRule) {
        if (request.RuleId != null) {
          this.$notification.error("Другое правило уже использует такие же параметры.");
          return;
        }
        request = new UpsertReferralRuleRequest({
          ...request,
          RuleId: Number(duplicateRule.Id),
        });
      }
      const conditionConflictRule = await this.findConditionConflictRule(request);
      if (conditionConflictRule) {
        this.$notification.error("Сервер сейчас не позволяет создать второе правило с таким же условием. Измените условие или отредактируйте существующее правило.");
        return;
      }
      const result = await this.$api.ReferralService.upsertRuleAsync(request);
      if (result) {
        this.$notification.success("Правило успешно сохранено.");
        this.closeForm();
        this.reloadTable();
      }
    } catch (error) {
      this.$notification.error((error as any).ErrorMessage || error);
    } finally {
      this.isFormSaving = false;
    }
  }

  async findDuplicateRule(request: UpsertReferralRuleRequest): Promise<ReferralRuleItem | null> {
    const currentRuleId = request.RuleId == null ? null : Number(request.RuleId);
    const currentKey = this.getRuleFullKey(request);
    const response = await this.$api.ReferralService.getRuleListAsync({
      Page: {
        Skip: 0,
        Take: 1000,
      },
    });
    const items = response && response.Items ? response.Items : [];

    return items.find((item) => {
      if (currentRuleId != null && Number(item.Id) === currentRuleId) {
        return false;
      }
      return this.getRuleFullKey(item) === currentKey;
    }) || null;
  }

  async findConditionConflictRule(request: UpsertReferralRuleRequest): Promise<ReferralRuleItem | null> {
    const currentRuleId = request.RuleId == null ? null : Number(request.RuleId);
    const currentKey = this.getRuleConditionKey(request);
    const response = await this.$api.ReferralService.getRuleListAsync({
      Page: {
        Skip: 0,
        Take: 1000,
      },
    });
    const items = response && response.Items ? response.Items : [];

    return items.find((item) => {
      if (currentRuleId != null && Number(item.Id) === currentRuleId) {
        return false;
      }
      return this.getRuleConditionKey(item) === currentKey;
    }) || null;
  }

  getRulePayload(rule: UpsertReferralRuleRequest | ReferralRuleItem) {
    const payload = buildUpsertReferralRulePayload({
      RuleId: (rule as any).RuleId == null ? (rule as any).Id : (rule as any).RuleId,
      Trigger: rule.Trigger,
      Threshold: rule.Threshold,
      WindowDays: rule.WindowDays,
      RewardType: rule.RewardType,
      RewardDays: rule.RewardDays,
    });

    return payload;
  }

  getRuleConditionKey(rule: UpsertReferralRuleRequest | ReferralRuleItem): string {
    const payload = this.getRulePayload(rule);

    return [
      payload.trigger,
      payload.threshold,
      payload.windowDays == null ? "" : payload.windowDays,
    ].join("|");
  }

  getRuleFullKey(rule: UpsertReferralRuleRequest | ReferralRuleItem): string {
    const payload = this.getRulePayload(rule);

    return [
      payload.trigger,
      payload.threshold,
      payload.windowDays == null ? "" : payload.windowDays,
      payload.rewardType,
      payload.rewardDays == null ? "" : payload.rewardDays,
    ].join("|");
  }

  async toggleRule(item: ReferralRuleItem) {
    const result = await this.$api.ReferralService.toggleRuleAsync({
      ruleId: item.Id,
      isActive: !item.IsActive,
    });
    if (result) {
      this.$notification.success("Статус правила изменен.");
      this.reloadTable();
    }
  }

  reloadTable() {
    const table = this.$refs.table as any;
    if (table && table.fetchData) {
      table.fetchData();
    }
  }
}
